/**
 * vue-i18n with ICU MessageFormat.
 *
 * vue-i18n's own message syntax is replaced by `intl-messageformat` (FormatJS)
 * through the `messageCompiler` hook, so every string is standard ICU — the
 * same syntax the Passport's and the modules' translators will see — and
 * plural/select rules come from CLDR via `Intl.PluralRules`. Numbers, dates
 * and money are formatted with the `Intl` APIs the locale implies
 * (`packages/money` `format(locale)`), never by hand.
 */
import IntlMessageFormat from "intl-messageformat";
import { createI18n, type CompileError, type I18n, type MessageCompiler, type MessageContext } from "vue-i18n";
import { DEFAULT_LOCALE, type ShellLocale } from "./locales";
import af from "./messages/af-ZA";
import en from "./messages/en-ZA";

export { DEFAULT_LOCALE, LOCALE_NAMES, SUPPORTED_LOCALES, isShellLocale, matchLocale } from "./locales";
export type { ShellLocale } from "./locales";

export const messages = { "en-ZA": en, "af-ZA": af };

/** vue-i18n reports compile failures as `CompileError`s; this code marks ours as ICU parse failures. */
export const ICU_COMPILE_ERROR_CODE = 9000;

function compileError(error: unknown): CompileError {
  const message = error instanceof Error ? error.message : String(error);
  return Object.assign(new SyntaxError(message), { code: ICU_COMPILE_ERROR_CODE });
}

/**
 * Compile one message with ICU MessageFormat. A message that will not parse
 * is reported through vue-i18n's `onError` and rendered as its source text —
 * a visitor sees the English pattern rather than an empty label.
 */
export const icuMessageCompiler: MessageCompiler = (message, { locale, key, onError }) => {
  if (typeof message !== "string") {
    onError?.(compileError(`Pre-compiled (AST) messages are not supported: ${key}`));
    return () => key;
  }
  let formatter: IntlMessageFormat;
  try {
    formatter = new IntlMessageFormat(message, locale);
  } catch (error) {
    onError?.(compileError(error));
    return () => message;
  }
  return (ctx: MessageContext) => {
    const formatted = formatter.format(ctx.values as Record<string, unknown>);
    return Array.isArray(formatted) ? formatted.join("") : String(formatted);
  };
};

export type ShellI18n = I18n<typeof messages, Record<string, never>, Record<string, never>, string, false>;

export function createShellI18n(locale: ShellLocale = DEFAULT_LOCALE): ShellI18n {
  return createI18n({
    legacy: false,
    locale,
    fallbackLocale: DEFAULT_LOCALE,
    messageCompiler: icuMessageCompiler,
    messages,
    // ICU strings are plain text; there is nothing to warn about, and the
    // console must stay clean (build plan §1.6).
    warnHtmlMessage: false,
    missingWarn: false,
    fallbackWarn: false,
  }) as ShellI18n;
}

/** The app's one instance; boot.ts installs it and sets the detected locale. */
export const i18n: ShellI18n = createShellI18n();
